import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Trash2, RotateCcw } from "lucide-react";
import { DELETE_REASON_OPTIONS } from "@/components/HistoryFilters";

/**
 * EXCLUSÃO CONTROLADA DE HISTÓRICO.
 *
 * Regras de UI alinhadas ao backend:
 *  - o motivo é OBRIGATÓRIO ("Outro" exige descrição);
 *  - ocorrência vinculada é consultada ANTES e a exclusão definitiva é
 *    bloqueada pelo backend;
 *  - excluir é SOFT DELETE (registro continua auditável);
 *  - restaurar é sempre possível para quem tem a permissão;
 *  - os botões aparecem conforme a PERMISSÃO, nunca por nome de cargo.
 */
export default function HistoryExcludeDialog({
  open,
  onOpenChange,
  record,
  module = "pga",
  onDone,
  canDelete = false,
  canRestore = false,
}) {
  const [reason, setReason] = useState("registro_de_teste");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState(null);

  useEffect(() => {
    if (!open || !record?.id) return;

    setReason("registro_de_teste");
    setDescription("");
    setLinked(null);

    api
      .get(`/history/${record.id}/occurrences`, { params: { module } })
      .then(({ data }) => setLinked(data || { count: 0, occurrences: [] }))
      .catch(() => setLinked({ count: 0, occurrences: [] }));
  }, [open, record?.id, module]);

  if (!open || !record) return null;

  const needsDescription = reason === "outro";
  const hasLinked = (linked?.count || 0) > 0;

  const doDelete = async () => {
    if (needsDescription && !description.trim()) {
      return toast.error("Descreva o motivo da exclusão.");
    }

    setSaving(true);

    try {
      const { data } = await api.delete(`/history/${record.id}`, {
        params: { module },
        data: { reason, description },
      });

      toast.success(data?.detail || "Registro excluído.");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Não foi possível excluir o registro."
      );
    } finally {
      setSaving(false);
    }
  };

  const doRestore = async () => {
    setSaving(true);

    try {
      await api.post(`/history/${record.id}/restore`, null, { params: { module } });
      toast.success("Registro restaurado no histórico.");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Não foi possível restaurar o registro."
      );
    } finally {
      setSaving(false);
    }
  };
return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="history-exclude-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {record.deleted ? "Restaurar registro" : "Excluir este monitoramento?"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-border px-3 py-2 text-xs space-y-0.5">
            <p className="font-medium">
              {record.structure_name || record.room_name || record.id}
            </p>
            <p className="text-muted-foreground">
              {record.inspector_name} ·{" "}
              {formatDate(record.performed_at || record.inspected_at || record.finished_at)}
            </p>
          </div>

          {record.deleted ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs space-y-1">
              <p><strong>Excluído por:</strong> {record.deleted_by_name || "—"}</p>
              <p><strong>Data:</strong> {formatDate(record.deleted_at)}</p>
              <p>
                <strong>Motivo:</strong>{" "}
                {DELETE_REASON_OPTIONS.find((o) => o.value === record.delete_reason)?.label ||
                  record.delete_reason ||
                  "—"}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              O registro deixará de aparecer no histórico operacional, mas continuará
              auditável (exclusão lógica). Fotos e evidências são preservadas.
            </p>
          )}

          {hasLinked && (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs flex items-start gap-2"
              data-testid="history-linked-warning"
            >
              <AlertTriangle size={14} className="text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Este registro possui ocorrência vinculada</p>
                <p className="font-mono">{linked.occurrences.map((o) => o.protocol).join(", ")}</p>
                <p className="text-muted-foreground mt-0.5">
                  A ocorrência é preservada. A exclusão definitiva fica bloqueada
                  enquanto houver vínculo.
                </p>
              </div>
            </div>
          )}

          {!record.deleted && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Motivo da exclusão</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="history-delete-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DELETE_REASON_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {needsDescription && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Descrição (obrigatória)</Label>
                  <Textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o motivo"
                    data-testid="history-delete-description"
                  />
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>

            {record.deleted
              ? canRestore && (
                  <Button size="sm" onClick={doRestore} disabled={saving} data-testid="history-restore-btn">
                    <RotateCcw size={14} className="mr-1.5" /> Restaurar
                  </Button>
                )
              : canDelete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={doDelete}
                    disabled={saving || (needsDescription && !description.trim())}
                    data-testid="history-delete-btn"
                  >
                    <Trash2 size={14} className="mr-1.5" /> Excluir registro
                  </Button>
                )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}